import PersonIcon from "@assets/svg/drawers/person.svg?react"
import LogoutIcon from "@assets/svg/navigation/logout.svg?react"
import PeopleIcon from "@assets/svg/people.svg?react"
import { AEMSvg } from "@components/AEMSvg"
import { useAEMIcons } from "@AEM/hooks/useAEMIcons"
import {
	Avatar,
	Badge,
	Box,
	Divider,
	Group,
	Stack,
	Text,
	UnstyledButton
} from "@mantine/core"
import styles from "./AccountMenu.module.css"
import { ReactElement, useState } from "react"
import {
	useAEMDictionary,
	AEM_DICTIONARY_COLLECTIONS
} from "@AEM/hooks/useAEMDictionary"

import {
	VITE_REDIRECT_NEW_MANAGEMENT,
	REDIRECT_TO_NEW_USER_PROFILE,
	USE_SUPER_USER,
	SHOW_MULTI_AGENCY
} from "@utils/constants.util"
import { useRedirect } from "@hooks/useRedirect"
import { LinkType } from "@models/link.model"
import { useAuthContext } from "@context/AuthProvider"
import { useUserStore } from "@hooks/useUserStore"
import { eventTrackingItems } from "@utils/eventTracking"
import { isAdminOrRCCLAdmin, isSuperUser } from "@utils/permission.util"
import { ChangeActiveAgencyModal } from "@pages/profile-managment/agencyAffiliationsSection/modals/ChangeActiveAgencyModal"

interface AccountMenuLinkInterface {
	icon: ReactElement
	text: string
	url: string
	type: LinkType
	color?: "blue" | "white"
	onClick?: () => void
}

function AccountMenuLink({
	icon,
	text,
	url,
	type,
	color = "white",
	onClick
}: AccountMenuLinkInterface) {
	const { handleRedirect } = useRedirect()

	const textColorClass =
		color === "blue" ? styles.accountMenuLinkAlt : styles.accountMenuLink
	const buttonColorClass =
		color === "blue" ? styles.accountMenuButtonBlue : styles.accountMenuButton

	const handleClick = () => {
		if (onClick) {
			onClick()
			return
		}

		eventTrackingItems({
			event_name: "click",
			link_url: url,
			category: "",
			interactionType: type,
			navLinkText: text
		})
		handleRedirect({ link: url, type: type })
	}

	return (
		<UnstyledButton
			onClick={handleClick}
			p="xl"
			className={buttonColorClass}
			w="100%"
		>
			<Group gap="lg" align="center">
				<Group className={textColorClass}>{icon}</Group>
				<Text fz={16} fw={600} className={textColorClass}>
					{text}
				</Text>
			</Group>
		</UnstyledButton>
	)
}

interface AccountMenuProps {
	onDrawerClose?: () => void
}

export function AccountMenu({ onDrawerClose }: AccountMenuProps) {
	const { userProfile, logout } = useAuthContext()
	const { dictionary: AEMDictionary = {} } = useAEMDictionary(
		AEM_DICTIONARY_COLLECTIONS.REGISTRATION
	)
	const { modifyAgentAccessText = "Agent Management" } = AEMDictionary
	const { dictionary } = useAEMDictionary()
	const { availableAffiliations } = useUserStore()

	const {
		agentFirstName,
		agentLastName,
		agentEmailAddress,
		agencyName,
		agentPhotos,
		roleCode
	} = userProfile || {}
	const fullPathProfilePath = agentPhotos?.AGENT_HOMEPAGE_PHOTO?.fullPath

	const [switchModalOpen, setSwitchModalOpen] = useState(false)

	const activeAffiliation =
		availableAffiliations.find((a) => a.active) ??
		availableAffiliations.find((a) => a.isDefault) ??
		null
	const displayAgencyName = activeAffiliation?.agencyName ?? agencyName ?? ""
	const isNonDefault = activeAffiliation != null && !activeAffiliation.isDefault
	const hasMultiple = availableAffiliations.length > 1
	const otherAffiliations = activeAffiliation
		? availableAffiliations.filter(
				(a) => a.agencyId !== activeAffiliation.agencyId
			)
		: availableAffiliations

	const { icons } = useAEMIcons()

	return (
		<>
			<Stack gap={0} h="100%" mih="100%" justify="space-between">
				<Stack gap={0}>
					<Group align="flex-start" wrap="nowrap" px="md" py="xl">
						<Avatar
							variant="transparent"
							src={fullPathProfilePath}
							size={40}
							color="var(--text-color-primary)"
						/>
						<Box>
							<Text fz={14} lh={1.4} fw={600}>
								{`${agentFirstName || ""} ${agentLastName || ""}`}
							</Text>
							<Text fz={14} fw={400} lh={1.4}>
								{agentEmailAddress}
							</Text>
							<Group gap={6} align="center" wrap="nowrap">
								<Text fz={12} fw={400} lh={1.7}>
									{displayAgencyName}
								</Text>
								{SHOW_MULTI_AGENCY &&
									userProfile?.hasMultiAgencyAccess &&
									hasMultiple &&
									isNonDefault && (
										<Badge size="xs" color="blue" variant="light" radius="sm">
											Active
										</Badge>
									)}
								{SHOW_MULTI_AGENCY &&
									userProfile?.hasMultiAgencyAccess &&
									hasMultiple &&
									!isNonDefault && (
										<Badge size="xs" color="gray" variant="light" radius="sm">
											Default
										</Badge>
									)}
							</Group>
						</Box>
					</Group>
					<Divider />
					<AccountMenuLink
						text={dictionary?.YourProfileLabel || ""}
						icon={<PersonIcon />}
						url={
							REDIRECT_TO_NEW_USER_PROFILE ? "/profile/info" : "your-profile"
						}
						type={REDIRECT_TO_NEW_USER_PROFILE ? "route" : "internal"}
					/>
					<Divider />
					{SHOW_MULTI_AGENCY &&
						userProfile?.hasMultiAgencyAccess &&
						hasMultiple && (
							<>
								<AccountMenuLink
									text={AEMDictionary?.maChangeCurrentAgencyHeader || ""}
									icon={
										<AEMSvg svgData={icons["switch-icon.svg"]} w={20} h={20} />
									} //change to the other arrows
									url=""
									type="route"
									onClick={() => setSwitchModalOpen(true)}
								/>
								<Divider />
							</>
						)}
					{isAdminOrRCCLAdmin(roleCode) && (
						<>
							<AccountMenuLink
								text={modifyAgentAccessText}
								icon={<PeopleIcon />}
								url={
									VITE_REDIRECT_NEW_MANAGEMENT
										? "/user-management"
										: "modify-agent-access"
								}
								type={VITE_REDIRECT_NEW_MANAGEMENT ? "route" : "internal"}
							/>
							<Divider />
						</>
					)}
				</Stack>
				<Stack gap={0}>
					<Divider />
					{isSuperUser(roleCode) && (
						<AccountMenuLink
							text={dictionary?.SuperUserLoginLabel || ""}
							icon={<PeopleIcon />}
							url={USE_SUPER_USER ? "/super-user" : "super-user-login"}
							type={USE_SUPER_USER ? "route" : "internal"}
						/>
					)}
					<Divider />
					<AccountMenuLink
						text={dictionary?.LogoutLabel || ""}
						url="/logout"
						icon={<LogoutIcon />}
						onClick={logout}
						color="blue"
						type="route"
					/>
				</Stack>
			</Stack>

			{activeAffiliation && (
				<ChangeActiveAgencyModal
					opened={switchModalOpen}
					onClose={() => setSwitchModalOpen(false)}
					currentAgency={activeAffiliation}
					otherAgencies={otherAffiliations}
					onDrawerClose={onDrawerClose}
				/>
			)}
		</>
	)
}
